import { BacklogTask, Review } from './types';

/**
 * Valor del filtro para "tareas sin proyecto". Hace falta un centinela porque
 * el proyecto vacío ya es '' y '' es el valor de "todos los proyectos".
 * No colisiona con un proyecto real: los nombres salen del catálogo
 * `public.proyecto`, que carga un admin desde Dimensiones.
 */
export const SIN_PROYECTO = '__sin_proyecto__';

export interface ReviewGroup {
  /** Nombre del proyecto; '' es el grupo "Sin proyecto". */
  name: string;
  revs: Review[];
}

/**
 * Revisiones pendientes que todavía corresponden a una tarea finalizada.
 * Defensivo: si un admin reabrió la tarea, su revisión no se muestra.
 */
function pendientesReales(
  pending: Review[],
  tasks: Record<string, BacklogTask>
): { r: Review; t: BacklogTask }[] {
  return pending
    .map((r) => ({ r, t: tasks[r.task_id] }))
    .filter((x): x is { r: Review; t: BacklogTask } => !!x.t && x.t.status === 'done');
}

/**
 * Proyectos que hoy tienen revisiones pendientes. Sólo se ofrecen esos: un
 * filtro que no devuelve nada no le sirve a nadie.
 */
export function proyectosConPendientes(
  pending: Review[],
  tasks: Record<string, BacklogTask>
): { nombres: string[]; haySinProyecto: boolean } {
  const nombres = new Set<string>();
  let haySinProyecto = false;
  for (const { t } of pendientesReales(pending, tasks)) {
    if (t.proyecto) nombres.add(t.proyecto);
    else haySinProyecto = true;
  }
  return { nombres: [...nombres].sort((a, b) => a.localeCompare(b)), haySinProyecto };
}

/**
 * Agrupa las revisiones pendientes por proyecto, con "Sin proyecto" al final.
 * `filtro`: '' son todos, SIN_PROYECTO son las que no tienen proyecto, y
 * cualquier otro valor es el nombre exacto de un proyecto.
 */
export function agruparRevisiones(
  pending: Review[],
  tasks: Record<string, BacklogTask>,
  filtro = ''
): ReviewGroup[] {
  const m = new Map<string, Review[]>();
  for (const { r, t } of pendientesReales(pending, tasks)) {
    const key = t.proyecto || '';
    if (filtro === SIN_PROYECTO && key !== '') continue;
    if (filtro && filtro !== SIN_PROYECTO && key !== filtro) continue;
    if (!m.has(key)) m.set(key, []);
    m.get(key)!.push(r);
  }
  return [...m.entries()]
    .sort((a, b) => (a[0] === '' ? 1 : b[0] === '' ? -1 : a[0].localeCompare(b[0])))
    .map(([name, revs]) => ({ name, revs }));
}
